import { useEffect, useState } from 'react';
// DTC-F: route the shared display-ticker through the canonical clock.
// This is a class-D-by-design display-only ticker (1s interval driving
// UI refresh) — production wall-clock behavior is identical. Routing
// through `clock.now()` lets consumers render this display in
// frozen-clock test fixtures without forking a render path.
import { clock } from '@/utils/clock';

// =====================================================================
// Module-level singleton state (1s + 60s cadences)
// =====================================================================
//
// One global setInterval per cadence regardless of how many components
// subscribe. Listener sets carry the per-subscriber callbacks; the
// `*IntervalId` handles are nullable so we can pause/resume on tab
// visibility changes without losing the subscriber set itself.

let globalTimestamp = clock.now();
const listeners: Set<() => void> = new Set();
let intervalId: ReturnType<typeof setInterval> | null = null;

let globalMinuteTimestamp = clock.now();
const minuteListeners: Set<() => void> = new Set();
let minuteIntervalId: ReturnType<typeof setInterval> | null = null;

// =====================================================================
// TIM-A3: shared visibility-change gating
// =====================================================================
//
// Pre-A3 both `setInterval` kernels fired their callbacks regardless of
// tab visibility, walking every subscriber's `setTimestamp(clock.now())`
// listener every 1s / 60s even when the tab was backgrounded. That is
// pure waste: backgrounded TicketCards / paused-optiFinish recalcs /
// predecessor-lock rechecks aren't user-visible, and the per-listener
// `setState` triggered React reconciliation across the unmounted /
// hidden subtree.
//
// The 60s polling hooks (`useAutoBump`, `useOptiFinishPushback`,
// `useConvertedRolloverGrowth`) already have a `document.hidden` early-
// return INSIDE their callbacks (V-E-2 doctrine), but the SHARED display
// timers do not — they kept ticking. TIM-A3 closes that gap at the
// timer kernel level so the cost shape is uniform across every shared-
// timer consumer.
//
// Behavior:
//   - When `document.hidden` becomes true: BOTH `setInterval` kernels
//     are cleared. Listener sets stay populated; subscribers stay
//     subscribed but receive no notifications while hidden.
//   - When `document.hidden` becomes false: subscribers receive a
//     single immediate "catch-up" notification with the current
//     `clock.now()` so any time-derived display is fresh, then the
//     `setInterval` kernels are re-armed for the steady-state cadence.
//   - The visibility listener is installed lazily (on first subscriber
//     mount) and detached only via `__TIM_A1_TEST_ONLY.reset()`. In
//     production it stays attached for the rest of the document's
//     lifetime — a single `visibilitychange` listener has no
//     measurable cost vs. the per-tick savings.
//   - Repeated `visibilitychange` events do NOT create duplicate
//     intervals: the start path checks `if (intervalId !== null)
//     return;` before scheduling, and the visibility listener short-
//     circuits the same way before re-arming.
//
// jsdom / SSR safety:
//   - `typeof document === 'undefined'` is treated as "not in a browser
//     environment" — the listener install becomes a no-op and the
//     timers behave exactly as pre-A3 (always-ticking). This keeps the
//     hook safe to import in Node-only test contexts and in any
//     hypothetical SSR pathway.
//   - `document.hidden` may be undefined in some jsdom configurations;
//     `isHidden()` only treats `=== true` as hidden, so undefined or
//     non-boolean falls through to the visible branch.

let visibilityListener: (() => void) | null = null;

function tick1s(): void {
  globalTimestamp = clock.now();
  listeners.forEach((listener) => listener());
}

function tick60s(): void {
  globalMinuteTimestamp = clock.now();
  minuteListeners.forEach((listener) => listener());
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

function ensureVisibilityListener(): void {
  if (visibilityListener !== null) return;
  if (
    typeof document === 'undefined' ||
    typeof document.addEventListener !== 'function'
  ) {
    return; // SSR / jsdom-without-document — leave the timers always-on.
  }
  visibilityListener = () => {
    if (isHidden()) {
      // Tab hidden — pause both kernels. Listener sets stay populated
      // so resumption is instant on visibility restore; we just stop
      // the wall-clock ticker that was driving them.
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (minuteIntervalId !== null) {
        clearInterval(minuteIntervalId);
        minuteIntervalId = null;
      }
      return;
    }
    // Tab visible — fire a single immediate catch-up notification per
    // cadence so subscribers re-read `clock.now()` after potentially
    // a long hidden period, then re-arm the steady-state kernels.
    if (listeners.size > 0) {
      tick1s();
      if (intervalId === null) {
        intervalId = setInterval(tick1s, 1000);
      }
    }
    if (minuteListeners.size > 0) {
      tick60s();
      if (minuteIntervalId === null) {
        minuteIntervalId = setInterval(tick60s, 60_000);
      }
    }
  };
  document.addEventListener('visibilitychange', visibilityListener);
}

function startGlobalTimer(): void {
  ensureVisibilityListener();
  if (intervalId !== null) return;
  if (isHidden()) {
    // Tab is currently hidden — defer the setInterval until the
    // visibility listener fires the show transition. The subscriber's
    // listener is already in `listeners`; `tick1s()` will be invoked
    // on the catch-up path then.
    return;
  }
  intervalId = setInterval(tick1s, 1000);
}

function stopGlobalTimer(): void {
  if (intervalId !== null && listeners.size === 0) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

export function useSharedTimer(): Date {
  const [timestamp, setTimestamp] = useState(globalTimestamp);

  useEffect(() => {
    const listener = () => setTimestamp(clock.now());
    listeners.add(listener);
    startGlobalTimer();

    return () => {
      listeners.delete(listener);
      stopGlobalTimer();
    };
  }, []);

  return timestamp;
}

// =====================================================================
// TIM-A1: shared minute-tick singleton
// =====================================================================
//
// `useSharedMinuteTimer` is the 60-second sibling of `useSharedTimer`.
// It runs ONE global setInterval(60_000) regardless of how many
// components subscribe, replacing the pre-A1 pattern where every
// `<TicketCard>` instance owned its own per-card 60s setInterval for
// (a) the paused-ticket optiFinish currentTime refresh and (b) the
// predecessor-lock check. Five tickets visible on a board previously
// meant five wall-clock-aligned timer kernels firing every 60s; with
// this hook all five subscribers receive the same shared timestamp from
// a single timer kernel.
//
// Notes on cadence semantics:
//   - The first tick fires 60s after the FIRST subscriber mounts (when
//     the global interval starts). Subsequent subscribers join the
//     existing cycle — they do NOT get their own immediate fire on
//     mount. Components that need a fresh value at mount-time should
//     read the returned `Date` and use it directly; the hook returns
//     the most recent global timestamp synchronously, not stale data.
//   - The interval is automatically stopped when the last subscriber
//     unmounts (`stopGlobalMinuteTimer` checks `minuteListeners.size`).
//     This keeps unmounted-suite test runs clean and avoids a stray
//     timer kernel after the last TicketCard unmounts.
//   - Routed through `clock.now()` for the same reason as the 1s
//     timer: frozen-clock test fixtures can drive minute-tick logic
//     without forking a render path.
//   - TIM-A3: the cadence is paused when the tab is hidden and resumed
//     with a single catch-up notification on visibility restore. See
//     the visibility-gating block above the 1s singleton for the full
//     contract; both cadences share one visibilitychange listener.
//
// Why a separate singleton (not parameterizing useSharedTimer):
//   - The two cadences serve different render-storm classes (1s for
//     live elapsed display, 60s for label refreshes). Keeping them as
//     siblings means future cadence-specific tuning (e.g. different
//     visibility-restore semantics, or per-cadence subscriber-count
//     metrics) can apply to one without affecting the other.

function startGlobalMinuteTimer(): void {
  ensureVisibilityListener();
  if (minuteIntervalId !== null) return;
  if (isHidden()) {
    // Same hidden-tab gating as the 1s sibling — defer until visible.
    return;
  }
  minuteIntervalId = setInterval(tick60s, 60_000);
}

function stopGlobalMinuteTimer(): void {
  if (minuteIntervalId !== null && minuteListeners.size === 0) {
    clearInterval(minuteIntervalId);
    minuteIntervalId = null;
  }
}

export function useSharedMinuteTimer(): Date {
  const [timestamp, setTimestamp] = useState(globalMinuteTimestamp);

  useEffect(() => {
    const listener = () => setTimestamp(clock.now());
    minuteListeners.add(listener);
    startGlobalMinuteTimer();

    return () => {
      minuteListeners.delete(listener);
      stopGlobalMinuteTimer();
    };
  }, []);

  return timestamp;
}

// Internal — exported solely for tests in __tests__/useSharedTimer.test.ts
// to assert the singleton invariant (listener-set size, interval handle
// presence/absence). Not part of the public hook API.
//
// `reset()` is a hard reset that drops all listeners, clears both
// global intervals, AND detaches the visibility listener (TIM-A3). Tests
// under `sequence.shuffle:true` (configured in vitest.config.ts) cannot
// rely on prior tests cleaning up before the next one runs — a leaked
// listener from one test would poison the singleton-count assertions of
// every subsequent test in the file. The reset is module-internal and
// must NEVER be called from production code; it is intentionally guarded
// behind the `__TIM_A1_TEST_ONLY` namespace so a stray
// `import { reset } from ...` never compiles.
export const __TIM_A1_TEST_ONLY = {
  getMinuteListenerCount: (): number => minuteListeners.size,
  hasMinuteInterval: (): boolean => minuteIntervalId !== null,
  getSecondListenerCount: (): number => listeners.size,
  hasSecondInterval: (): boolean => intervalId !== null,
  hasVisibilityListener: (): boolean => visibilityListener !== null,
  reset: (): void => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (minuteIntervalId !== null) {
      clearInterval(minuteIntervalId);
      minuteIntervalId = null;
    }
    listeners.clear();
    minuteListeners.clear();
    // TIM-A3: detach the visibility listener so each test starts with a
    // clean module state. Without this, the listener installed by one
    // test would still be attached when the next test runs, and
    // dispatching a `visibilitychange` event for that next test's
    // assertions would also fire the prior test's stale listener (it
    // would be a no-op because we cleared the listener-set, but it
    // would also re-anchor the visibility-listener install flag,
    // making the install-once invariant un-testable).
    if (
      visibilityListener !== null &&
      typeof document !== 'undefined' &&
      typeof document.removeEventListener === 'function'
    ) {
      document.removeEventListener('visibilitychange', visibilityListener);
    }
    visibilityListener = null;
    // Re-anchor the cached "last known" timestamps to the current clock
    // value. Both `globalTimestamp` and `globalMinuteTimestamp` are
    // captured at module-load time as the mount-time defaults for
    // `useState(...)`. Without this re-anchor, a test that calls
    // `clock.setFixed(...)` AFTER module load would still see hooks
    // mount with the module-load wall-clock value (since useState's
    // initializer reads the cached anchor, not `clock.now()`). Re-
    // anchoring here makes `clock.setFixed(FIXED_BASE)` followed by
    // `__TIM_A1_TEST_ONLY.reset()` produce the deterministic state a
    // beforeEach hook needs.
    globalTimestamp = clock.now();
    globalMinuteTimestamp = clock.now();
  },
};
